import { useEffect } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useStore } from "../store/useStore";

const SCOPES = [
  "openid",
  "email",
  "profile",
  "https://www.googleapis.com/auth/calendar.events.readonly",
];

// Once per browser tab, after capturing the refresh token, skip future calls.
// The token only ever appears in the session immediately after OAuth — on
// later page loads `getSession()` may not return it. We persist server-side
// so the calendar Edge Function can refresh access tokens transparently.
let storedRefreshTokenThisTab = false;

async function captureGoogleRefreshToken(session: Session) {
  if (storedRefreshTokenThisTab) return;
  const refreshToken = session.provider_refresh_token;
  if (!refreshToken) return;

  storedRefreshTokenThisTab = true;
  try {
    const baseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!baseUrl) {
      storedRefreshTokenThisTab = false;
      return;
    }
    const res = await fetch(`${baseUrl}/functions/v1/store-google-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        provider_refresh_token: refreshToken,
        scopes: SCOPES,
      }),
    });

    // fetch() does not throw on non-2xx responses (only on network errors), so
    // we must explicitly check `res.ok`. Without this, a transient 5xx from
    // store-google-token would be silently treated as success and the
    // tab-scoped guard would prevent any retry — defeating the transparent
    // refresh path.
    if (!res.ok) {
      storedRefreshTokenThisTab = false;
      console.error(
        "Failed to store Google refresh token: HTTP",
        res.status,
        await res.text().catch(() => "")
      );
    }
  } catch (err) {
    // Network-level failure (DNS, offline, CORS). Reset so a future
    // SIGNED_IN / INITIAL_SESSION event can retry.
    storedRefreshTokenThisTab = false;
    console.error("Failed to store Google refresh token:", err);
  }
}

export function AuthListener() {
  const setAuth = useStore((state) => state.setAuth);

  useEffect(() => {
    const fallbackTimeout = setTimeout(() => {
      setAuth(null, null);
    }, 5000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        clearTimeout(fallbackTimeout);
        setAuth(session.user, session);
        // INITIAL_SESSION arrives before this in some cases, but we also try
        // here for cold-load situations where the token still happens to be
        // present.
        captureGoogleRefreshToken(session);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || (event === "INITIAL_SESSION" && session)) {
        clearTimeout(fallbackTimeout);
        setAuth(session!.user, session!);
        captureGoogleRefreshToken(session!);
        return;
      }

      if (event === "SIGNED_OUT") {
        clearTimeout(fallbackTimeout);
        storedRefreshTokenThisTab = false;
        setAuth(null, null);
        return;
      }

      if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        if (session) {
          setAuth(session.user, session);
        }
        return;
      }
    });

    return () => {
      clearTimeout(fallbackTimeout);
      subscription.unsubscribe();
    };
  }, [setAuth]);

  return null;
}
